import {InputGroup} from '@/components/app/input-group';
import {toastError} from '@/app/toast';
import {Fragment, SyntheticEvent, useEffect, useMemo, useRef, useState} from 'react';
import {ChoiceSelect} from '@/components/app/choice-select';
import {Input} from '@/components/ui/input';
import {SearchPopover} from '@/components/app/search-popover';
import {LoadingSkeleton} from '@/components/app/feedback';
import {cn} from '@/lib/utils';
import {VirtualList} from '@/components/app/virtual-list';
import {Icon} from '../../../app/components/Icon';
import {TextMatch} from '../../../app/components/TextMatch';
import {useGetRelevantConditionsQuery} from '../../../app/services/biotwinApi';
import {
  useGetConditionTypesQuery,
  useLazyGetConditionsDataCountQuery,
  useLazyGetConditionsQuery,
} from '../../../app/services/conditionApi';
import {MatchMode} from '../../../app/types';
import {getTextMatchNormalizedMatcher, getTextMatchNormalizedText} from '../../../app/utils/miscUtils';
import {Condition, ConditionTypeName} from '../types';
import {getRequiredRelatedRelevantConditions, getSelectedRelevantConditions} from '../utils';
import {ConditionTypeTag} from './ConditionTypeTag';
import styles from './ConditionSearchBar.module.css';
import {useIsAdmin} from '../../auth/hooks/useIsAdmin';
const ITEM_SIZE = 58;
const PAGE_SIZE = 15;
const PLACEHOLDER_RESULT = undefined;
export interface ConditionSearchBarProps {
  className?: string;
  disabled?: boolean;
  dropdownClassName?: string;
  fieldClassName?: string;
  initialConditionTypeId?: number;
  inputClassName?: string;
  inputPlaceholder?: string;
  onSelect: (event: {value: Condition}) => void;
  similarityThreshold?: number;
  threshold?: number;
  userId: number;
}
export const ConditionSearchBar = ({
  className,
  disabled,
  dropdownClassName,
  fieldClassName,
  initialConditionTypeId,
  inputClassName,
  inputPlaceholder = 'Search features to add this biotwin',
  onSelect,
  similarityThreshold,
  threshold = 1,
  userId,
}: ConditionSearchBarProps) => {
  const isAdmin = useIsAdmin();
  const [searchQuery, setSearchQuery] = useState('');
  const [lazyResults, setLazyResults] = useState<any[]>([]);
  const [fetchingResults, setFetchingResults] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const searchGeneration = useRef(0);
  const [conditionTypeId, setConditionTypeId] = useState<number | undefined>(initialConditionTypeId);
  const searchTimeoutId = useRef(-1);
  const element = useRef<HTMLDivElement>(null);
  const inputText = useRef<HTMLInputElement>(null);
  const overlayPanel = useRef<HTMLDivElement>(null);
  const loadingPageNumbers = useRef<number[]>([]);
  const searchQueryRef = useRef('');
  const {data: conditionTypes = []} = useGetConditionTypesQuery();
  const {data: relevantConditions = []} = useGetRelevantConditionsQuery(
    {
      userId,
    },
    {
      skip: !userId,
    },
  );
  const [getConditions] = useLazyGetConditionsQuery();
  const [getConditionsDataCount] = useLazyGetConditionsDataCountQuery();
  const searchQueryNormalizedMatcher = useMemo(() => getTextMatchNormalizedMatcher(searchQuery), [searchQuery]);
  const selectedRelevantConditions = getSelectedRelevantConditions(relevantConditions);
  const requiredRelatedRelevantConditions = getRequiredRelatedRelevantConditions(relevantConditions);
  searchQueryRef.current = searchQuery;
  useEffect(() => {
    const generation = ++searchGeneration.current;
    setFetchingResults(false);
    loadingPageNumbers.current = [];
    setLazyResults([]);
    if (!searchQuery.trim() || searchQuery.trim().length < threshold) {
      setLazyResults([]);
      setResultsOpen(false);
      return;
    }
    let cancelled = false;
    searchTimeoutId.current = window.setTimeout(() => {
      setFetchingResults(true);
      Promise.all([
        getConditions({
          conditionTypeId,
          searchTerm: searchQuery,
          searchTermMatchMode: isAdmin ? MatchMode.Similarity : MatchMode.Contains,
          similarityThreshold,
          pageNumber: 1,
          pageSize: PAGE_SIZE,
          userId,
        }).unwrap(),
        getConditionsDataCount({
          conditionTypeId,
          searchTerm: searchQuery,
          searchTermMatchMode: isAdmin ? MatchMode.Similarity : MatchMode.Contains,
          similarityThreshold,
          userId,
        }).unwrap(),
      ])
        .then(([conditions, dataCount]) => {
          if (!cancelled) {
            if (element.current) {
              setResultsOpen(true);
              setTimeout(() => {
                if (cancelled || generation !== searchGeneration.current) return;
                setLazyResults([
                  ...(conditions ?? []),
                  ...(Array.from({
                    length: dataCount - (conditions?.length ?? 0),
                  }).fill(PLACEHOLDER_RESULT) as any[]),
                ]);
              }, 10);
            }
          }
        })
        .catch(console.error)
        .finally(() => {
          if (!cancelled) setFetchingResults(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      searchGeneration.current++;
      window.clearTimeout(searchTimeoutId.current);
    };
  }, [
    isAdmin,
    conditionTypeId,
    getConditions,
    getConditionsDataCount,
    searchQuery,
    similarityThreshold,
    threshold,
    userId,
  ]);
  return (
    <div className={cn(className, 'flex flex-col gap-2 px-4 sm:flex-row sm:gap-0 sm:p-0')} ref={element}>
      <ChoiceSelect
        data-name="condition-search-bar-dropdown"
        className={cn(
          styles.dropdown,
          'hidden sm:flex w-full sm:w-[13rem] sm:rounded-r-none shadow-none',
          dropdownClassName,
        )}
        disabled={disabled}
        onValueChange={(e) => setConditionTypeId(e === 'all' ? undefined : Number(e))}
        options={[
          {
            label: 'All',
            value: 'all',
          },
          ...(conditionTypes
            ?.filter((conditionType) => conditionType.conditionTypeName !== ConditionTypeName.Category)
            .map((conditionType) => ({
              label: conditionType.conditionTypeName,
              value: conditionType.id,
            })) ?? []),
        ]}
        optionLabel="label"
        optionValue="value"
        panelClassName={styles.dropdownPanel}
        value={conditionTypeId ?? 'all'}
      />
      <div className={cn(styles.field, 'min-w-0 sm:flex-1', fieldClassName)} data-name="condition-search-bar-input">
        <InputGroup position="right" className="w-full">
          <Input
            className={cn(styles.input, inputClassName, 'sm:rounded-l-none w-full')}
            disabled={disabled}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                const overlayPanelElement = overlayPanel.current;
                if (overlayPanelElement) {
                  const firstResult = overlayPanelElement.querySelector('.condition-search-bar__result:first-child');
                  if (firstResult instanceof HTMLElement) {
                    firstResult.focus();
                    e.preventDefault();
                  }
                }
              } else if (e.key === 'Escape') {
                setLazyResults([]);
                setSearchQuery('');
                setResultsOpen(false);
              }
            }}
            aria-label={inputPlaceholder}
            placeholder={inputPlaceholder}
            ref={inputText}
            value={searchQuery}
          />
          {fetchingResults && <Icon icon="spinner" spin />}
          {!fetchingResults && <Icon icon="search" />}
        </InputGroup>
      </div>
      <SearchPopover
        anchorRef={element}
        open={resultsOpen}
        onOpenChange={setResultsOpen}
        className={cn(styles.overlayPanel, 'rounded-xl')}
        ref={overlayPanel}
      >
        {lazyResults.length > 0 && (
          <VirtualList
            items={lazyResults}
            itemHeight={ITEM_SIZE}
            renderItem={(result: Condition | typeof PLACEHOLDER_RESULT, options) => {
              if (result === PLACEHOLDER_RESULT) {
                return (
                  <div
                    className={cn('p-4 border-b border-zinc-300 flex flex-col justify-center gap-2', {
                      'rounded-t-xl': options.first,
                      'rounded-b-xl': options.last,
                    })}
                    key={options.index}
                    style={{
                      height: ITEM_SIZE,
                    }}
                  >
                    <LoadingSkeleton height="1rem" width="32rem" />
                    <LoadingSkeleton height="1rem" width="8rem" />
                  </div>
                );
              }
              const selected = selectedRelevantConditions.some(
                ({conditionId, drugName}) => conditionId === result.id && drugName === result.drugName,
              );
              const required = requiredRelatedRelevantConditions.some(({conditionId}) => conditionId === result.id);
              const disabled = selected || required;
              const text = result.drugName ?? result.conditionName;
              return (
                <div
                  className={cn(
                    'condition-search-bar__result flex items-center gap-4 cursor-pointer px-4 border-b border-zinc-300 hover:bg-zinc-100 focus:shadow-none focus:bg-blue-50 active:shadow-none',
                    {
                      'rounded-t-xl': options.first,
                      'rounded-b-xl': options.last,
                      'pointer-events-none text-zinc-500 bg-zinc-50': disabled,
                    },
                  )}
                  data-result-index={options.index}
                  aria-disabled={disabled}
                  key={result.id}
                  onClick={() => {
                    if (!disabled) {
                      onSelect({
                        value: result,
                      });
                      setLazyResults([]);
                      setSearchQuery('');
                      setResultsOpen(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp' && options.index === 0) {
                      e.preventDefault();
                      inputText.current?.focus();
                    } else if (e.key === 'Enter') {
                      if (!disabled) {
                        e.currentTarget.click();
                      }
                    } else if (e.key === 'Escape') {
                      setLazyResults([]);
                      setSearchQuery('');
                      setResultsOpen(false);
                    }
                  }}
                  role={'button'}
                  style={{
                    height: ITEM_SIZE,
                    outline: 0,
                  }}
                  tabIndex={0}
                >
                  <div className="flex-1 flex flex-col gap-1">
                    <span>
                      <TextMatch
                        matcher={searchQueryNormalizedMatcher}
                        referenceText={getTextMatchNormalizedText(text)}
                        text={text}
                      />
                    </span>
                    {result.synonymNames.length > 0 && result.drugId == null && (
                      <div className="text-sm text-gray-500">
                        (
                        {result.synonymNames.map((synonymName, index) => (
                          <Fragment key={synonymName}>
                            {index > 0 ? ', ' : ''}
                            <span>
                              <TextMatch
                                matcher={searchQueryNormalizedMatcher}
                                referenceText={getTextMatchNormalizedText(synonymName)}
                                text={synonymName}
                              />
                            </span>
                          </Fragment>
                        ))}
                        )
                      </div>
                    )}
                  </div>
                  {result.drugId != null && (
                    <span className={'text-xs text-foreground'}>
                      <Icon className={'text-xs rotate-180'} icon={'filter'} /> {result.conditionName}
                    </span>
                  )}
                  {(selected || required) && (
                    <span className={'text-xs text-foreground'}>
                      <Icon className={'text-xs'} icon={'tag'} /> Already Added
                    </span>
                  )}
                  <ConditionTypeTag className="whitespace-nowrap" conditionTypeName={result.conditionTypeName} />
                </div>
              );
            }}
            onRangeChange={async ({startIndex, endIndex}) => {
              const generation = searchGeneration.current;
              const pages = new Set<number>();
              for (let index = startIndex; index < endIndex; index++) {
                if (lazyResults[index] === PLACEHOLDER_RESULT) pages.add(Math.floor(index / PAGE_SIZE) + 1);
              }
              await Promise.all(
                Array.from(pages).map(async (pageNumber) => {
                  if (loadingPageNumbers.current.includes(pageNumber)) return;
                  loadingPageNumbers.current.push(pageNumber);
                  try {
                    const results = await getConditions({
                      conditionTypeId,
                      searchTerm: searchQuery,
                      searchTermMatchMode: isAdmin ? MatchMode.Similarity : MatchMode.Contains,
                      similarityThreshold,
                      pageNumber,
                      pageSize: PAGE_SIZE,
                      userId,
                    }).unwrap();
                    if (generation === searchGeneration.current && searchQueryRef.current === searchQuery) {
                      setLazyResults((items) => [
                        ...items.slice(0, (pageNumber - 1) * PAGE_SIZE),
                        ...results,
                        ...items.slice((pageNumber - 1) * PAGE_SIZE + results.length),
                      ]);
                    }
                  } catch (error) {
                    if (generation === searchGeneration.current) toastError(error);
                  } finally {
                    if (generation === searchGeneration.current)
                      loadingPageNumbers.current = loadingPageNumbers.current.filter((page) => page !== pageNumber);
                  }
                }),
              );
            }}
            style={{
              height: Math.min(
                lazyResults.length * ITEM_SIZE,
                window.innerHeight - (element.current?.getBoundingClientRect().bottom ?? 0) - 8,
              ),
            }}
          />
        )}
        {lazyResults.length === 0 && <div className="p-4 text-muted-foreground">No results</div>}
      </SearchPopover>
    </div>
  );
};
